import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import InfoIcon from '@mui/icons-material/Info';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import CompositeDialog from '../dialog/compositeDialog';
import { RichTextEditorRef } from './rich-text-editor';

import {
  createVisitSuccess,
  deleteVisit,
  updateVisitSuccess,
} from 'redux/slices/visitsSlice';
import { useAppDispatch } from 'redux/store-hooks';

import { Permission } from '@act/shared/authorization';
import { VISIT_INSUFFICIENT_NOTES_OUTCOMES } from '@act/shared/constants';
import {
  CreateVisitForm,
  CreateVisitSchema,
  NeedDto,
  ResourceLocationDto,
  ScheduledEventDto,
  UserNeedDto,
  VisitCreationRequestDto,
  VisitDto,
  VisitUpdateRequestDto,
} from '@act/shared/data-transfer-objects';
import { FormBuilder, SelectOption } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { TrackingProvider } from 'react-components/tracking';
import { visitDurations } from 'redux/constants/durations-data';
import { metaSliceSelectors } from 'redux/slices/metaSlice';
import { autoSaveNotesRequests } from 'redux/requests/auto-save.requests';
import { visitsRequests } from 'redux/requests/visits.request';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { getEnvironment } from 'toolkit/environment/environment/environment';
import {
  DATE_TIME_FORMAT,
  visitDateTimeHelpers,
} from 'utilities/date-time-generator';
import { AddressFormatter } from 'utilities/formatters/address-formatter';
import { useToast } from '../app/ReusableToast';
import { DialogFooterAction } from '../dialog/dialog-footer';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import useConfirmEngagementDialog from './hooks/use-confirm-engagement-dialog';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';

export const enum VisitMode {
  CREATE = 'Create',
  EDIT = 'Edit',
}

export type ResourceCommunicationDetails = {
  title: string;
  description: string;
  findHelpId: string;
  location: ResourceLocationDto;
  need?: UserNeedDto;
  isCustomResourcing?: boolean;
};

export type CreateVisitData = {
  mode: VisitMode.CREATE;
  resource?: ResourceCommunicationDetails;
  scheduledEventId?: number;
};

export type EditVisitData = {
  mode: VisitMode.EDIT;
  visit: VisitDto;
};

export interface VisitDialogInterfaceProps {
  userId: string;
  visit?: VisitDto;
  resourceDetails?: ResourceCommunicationDetails;
  scheduledEvent?: ScheduledEventDto;
  userNeed?: UserNeedDto;
  isCustomResourcing?: boolean;
  need?: NeedDto;
  fromResourceHub?: boolean;
  completionCallback?: () => void;
}

export type VisitDialogData = CreateVisitData | EditVisitData;

interface VisitDialogProps extends VisitDialogInterfaceProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit';
  open: boolean;
}

export interface VisitForm {
  date: string;
  duration: number;
  addressId: number;
  location: string;
  note: string;
  outcomeId: number;
  start: number;
  resourceName: string;
  userNeedId: number;
  needCategory: number;
  needStatus: string;
  needTitle: string;
}

const VisitDialog = ({
  open,
  scheduledEvent,
  handleClose,
  userId,
  visit,
  mode,
  completionCallback,
}: VisitDialogProps) => {
  const openConfirmEngagementDialog = useConfirmEngagementDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const environment = getEnvironment();
  const algorithmStartDate = DateTime.fromFormat(
    environment.memberStatusAlgorithmStartDate,
    DATE_TIME_FORMAT
  );

  const user = membersAppSelectors.useSelectMemberById(userId);
  const toast = useToast();
  const dispatch = useAppDispatch();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const isManager = membersAppSelectors.useHasPermissionOnMember(
    userId,
    Permission.CAN_AUDIT_MEMBER_STATUS_CHANGE_REQUESTS
  );
  const visitTimestamp = visit?.startTime
    ? DateTime.fromMillis(visit?.startTime)
    : null;
  const isSameYear =
    visitTimestamp && visitTimestamp?.year === DateTime.now().year;
  const isSameMonth =
    visitTimestamp && visitTimestamp?.month === DateTime.now().month;
  const isSameDay =
    visitTimestamp && visitTimestamp?.day === DateTime.now().day;
  const canDelete =
    !!visit?.id &&
    isSameYear &&
    ((visit?.createdBy === actingUser.id && isSameDay) ||
      (isManager && isSameMonth));
  const outcomes = metaSliceSelectors.useSelectVisitOutcomes();

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [isManualSave, setIsManualSave] = useState(false);
  const richTextEditorRef = useRef<RichTextEditorRef>(null);
  const dateOptions: SelectOption<string>[] = useMemo(
    () => visitDateTimeHelpers.getDateOptions(visit?.startTime),
    [visit?.startTime]
  );
  const startTimeOptions: SelectOption<number>[] = useMemo(
    () => visitDateTimeHelpers.getTimeOptions(visit?.startTime),
    [visit?.startTime]
  );
  const defaultStartTime: number | string = useMemo(() => {
    // Get the time that is most recent
    const now = visitDateTimeHelpers.getTimeValue(new Date().getTime());
    for (let i = 0; i < startTimeOptions.length; i++) {
      if ((startTimeOptions[i].value as number) > now) {
        if (i === 0) {
          return startTimeOptions[0].value;
        } else {
          return startTimeOptions[i - 1].value;
        }
      }
    }
    return startTimeOptions[startTimeOptions.length - 1].value;
  }, [startTimeOptions]);

  const addressOptions: SelectOption<number>[] = useMemo(() => {
    const curatedAddresses = (user?.addresses || [])?.map(address => {
      return {
        label: `${AddressFormatter.toLong(address)}`,
        value: address?.id,
      };
    });
    return [...curatedAddresses, { label: 'Other', value: -1 }];
  }, [user?.addresses]);

  const [_deletedAutoSave, _isAutoSaveDeleting, deleteAutoSave] = useApiCall(
    autoSaveNotesRequests.deleteAutoSavedNote
  );

  const [autoSavedNote, _isLoadingGet, getAutoSavedNote] = useApiCall(
    autoSaveNotesRequests.getAutoSavedNote
  );

  const [_autoSaveAction, _isLoadingSave, postAutoSavedNote] = useApiCall(
    autoSaveNotesRequests.postAutoSavedNote
  );

  const [createdVisit, _isLoadingCreate, createVisit] = useApiCall(
    visitsRequests.createVisit
  );
  const [updatedVisit, _isLoadingUpdate, updateVisit] = useApiCall(
    visitsRequests.updateVisit
  );

  // Computed loading state from API calls
  const isSaving = _isLoadingCreate || _isLoadingUpdate;

  const { control, handleSubmit, setValue, watch, formState } =
    useForm<CreateVisitForm>({
      defaultValues: {
        date: visit?.startTime
          ? visitDateTimeHelpers.getDateString(visit.startTime)
          : dateOptions[0]?.value,
        start: visit?.startTime
          ? visitDateTimeHelpers.getTimeValue(visit.startTime)
          : defaultStartTime,
        addressId:
          mode === VisitMode.EDIT
            ? visit?.addressId ?? -1
            : addressOptions[0]?.value,
        location: visit?.location ?? null,
        note:
          mode === VisitMode.EDIT
            ? visit?.note
            : autoSavedNote?.text ?? '<p><br></p>',
        outcomeId: visit?.outcomeId ?? null,
        duration: visit?.duration ?? 15 * 60 * 1000,
      },
      resolver: zodResolver(CreateVisitSchema),
    });
  const selectedOutcomeId = watch('outcomeId');
  const addressId = watch('addressId');

  const canEditEngagement = visit
    ? DateTime.fromMillis(visit?.startTime) > algorithmStartDate
    : true;

  const outcomeOptions = metaSliceSelectors.useSelectVisitOutcomeOptions(
    'interaction',
    visit?.startTime
  );

  const outcomeHasCharacterMinimum: boolean = useMemo(() => {
    if (!outcomes || !selectedOutcomeId) {
      return false;
    }
    const outcome = outcomes.find(o => o.id === selectedOutcomeId);
    return VISIT_INSUFFICIENT_NOTES_OUTCOMES.includes(outcome.key);
  }, [outcomes, selectedOutcomeId]);

  useEffect(() => {
    if (addressId !== -1) setValue('location', null);
  }, [addressId]);

  const handleDeleteVisit = () => {
    dispatch(
      deleteVisit({
        params: {
          visitId: visit.id,
        },
      })
    );
    handleClose();
  };

  async function handleSave(data: CreateVisitForm) {
    if (isSaving) return;
    if (richTextEditorRef.current) {
      richTextEditorRef.current.cancelPendingAutoSave();
    }
    let currentNoteValue: string | undefined;
    if (richTextEditorRef.current) {
      currentNoteValue = richTextEditorRef.current.getCurrentValue();
      data.note = currentNoteValue;
    }
    const selectedOutcome = outcomes.find(
      outcome => outcome.id === data.outcomeId
    );
    if (
      user?.memberStage.key === 'outreach' &&
      selectedOutcome?.key === 'engaged'
    ) {
      const confirmed = await openConfirmEngagementDialog();
      if (!confirmed) {
        return;
      }
    }

    const startMillisFromMidnight = data.start;
    const durationMillis = data.duration;
    const dateString = data.date;
    const dateMillis =
      visitDateTimeHelpers.epochMillisFromDateString(dateString);

    const startTimeMillis = dateMillis + startMillisFromMidnight;
    const endTimeMillis = startTimeMillis + durationMillis;

    const body: VisitCreationRequestDto | VisitUpdateRequestDto = {
      ...(!!visit && { visitId: visit?.id }),
      subjectUserId: user.id,
      description: null,
      addressId: data?.addressId !== -1 ? data.addressId : null,
      location: data?.location ?? null,
      startTime: startTimeMillis,
      endTime: endTimeMillis,
      outcomeId: data.outcomeId,
      outcomeDetails: selectedOutcome?.text ?? '',
      note: data.note,

      scheduledEventId: mode === VisitMode.CREATE ? scheduledEvent?.id : null,
    };

    try {
      if (mode === VisitMode.EDIT) {
        setIsManualSave(true);
        await updateVisit({ body, params: { visitId: visit?.id } });
      } else {
        await createVisit({ body });
        await deleteAutoSave({
          params: { subjectId: user?.id, type: 'visits' },
        });
      }
      if (richTextEditorRef.current && currentNoteValue) {
        richTextEditorRef.current.markAsSaved(currentNoteValue);
      }
    } catch (err) {
      console.error(`Error saving visit: ${err}`);
      setIsManualSave(false);
    }

    if (completionCallback) {
      completionCallback();
    }
  }

  useEffect(() => {
    if (mode === VisitMode.EDIT) return;
    async function getAutoSaved() {
      try {
        await getAutoSavedNote({
          params: { subjectId: user?.id, type: 'visits' },
        });
      } catch (err) {
        console.error(`Error getting autosaved note: ${err}`);
      }
    }
    void getAutoSaved();
  }, []);

  useEffect(() => {
    if (!autoSavedNote) return;
    setValue('note', autoSavedNote.text);
  }, [autoSavedNote]);

  async function handleAutoSave(autoSaveNote: string) {
    if (mode === VisitMode.CREATE) {
      postAutoSavedNote({
        params: { subjectId: user?.id, type: 'visits' },
        body: { text: autoSaveNote },
      });
    } else {
      const body = { note: autoSaveNote };
      const params = { visitId: visit?.id };
      updateVisit({ body, params });
    }
  }

  const title = `${mode === VisitMode.CREATE ? 'New' : 'Edit'} Visit for ${
    user?.firstName
  } ${user?.lastName}`;

  const actions: DialogFooterAction[] = [
    {
      text: 'Cancel',
      callback: () => handleFormClose(),
      variant: 'outlined',
    },
    {
      text: isSaving ? 'Saving...' : 'Save',
      type: 'submit',
      variant: 'contained',
      disabled: isSaving,
    },
  ];
  if (canDelete) {
    actions.unshift({
      text: 'Delete',
      isLeft: true,
      callback: () => setConfirmDeleteOpen(true),
    });
  }

  useEffect(() => {
    // Handle successful API responses
    if (createdVisit) {
      dispatch(createVisitSuccess(createdVisit));
      toast('Visit created successfully');
      handleClose();
    }
    if (updatedVisit && isManualSave) {
      dispatch(updateVisitSuccess(updatedVisit));
      toast('Visit updated successfully');
      handleClose();
    } else if (updatedVisit && !isManualSave) {
      setIsManualSave(false);
    }
  }, [createdVisit, updatedVisit, isManualSave]);

  const formConfig: FormConfig<CreateVisitForm> = [
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: 'date',
          label: 'Date',
          input: 'select',
          disabled: !canEditEngagement,
          props: {
            options: dateOptions,
            trackingName: 'visit-date-select',
          },
        },
        {
          type: 'input',
          name: 'start',
          label: 'Start Time',
          input: 'select',
          size: 1,
          disabled: !canEditEngagement,
          props: {
            options: startTimeOptions,
            trackingName: 'visit-start-time-select',
          },
        },
        {
          type: 'input',
          name: 'duration',
          label: 'Duration',
          input: 'select',
          size: 1,
          disabled: !canEditEngagement,
          props: {
            options: visitDurations,
            trackingName: 'visit-duration-select',
          },
        },
      ],
    },
    {
      type: 'input',
      name: 'addressId',
      label: 'Location',
      input: 'select',
      props: {
        options: addressOptions,
        trackingName: 'visit-location-select',
      },
    },
    ...(addressId === -1
      ? [
          {
            type: 'input' as const,
            name: 'location' as const,
            label: 'Other Location',
            input: 'text' as const,
            disabled: !canEditEngagement,
            props: {
              trackingName: 'visit-other-location-input',
            },
          },
        ]
      : []),
    {
      type: 'input',
      name: 'outcomeId',
      label: 'Outcome',
      input: 'select',
      disabled: !canEditEngagement,
      props: {
        options: outcomeOptions,
        trackingName: 'visit-outcome-select',
      },
    },
    {
      type: 'passthrough',
      content: outcomeHasCharacterMinimum && (
        <Alert severity="info" icon={<InfoIcon fontSize="inherit" />}>
          <Typography variant="bodySmall">
            For this outcome a note of 50 characters or more is required.
          </Typography>
        </Alert>
      ),
    },
    {
      type: 'input',
      name: 'note',
      label: 'Note',
      input: 'richText',
      props: {
        autoSaveFn: handleAutoSave,
        showCharacterCount: true,
        trackingName: 'visit-note-input',
      },
    },
  ];

  const { isDirty } = formState;
  const handleFormClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        deleteAutoSave({
          params: { subjectId: user?.id, type: 'visits' },
        });
        handleClose();
      });
    } else {
      handleClose();
    }
  };

  return (
    <TrackingProvider contextName="visit-dialog">
      <CompositeDialog
        handleClose={handleFormClose}
        maxWidth="md"
        open={open}
        title={title}
        icon={<BusinessOutlinedIcon color="primary" sx={{ marginRight: 1 }} />}
        handleFormSubmit={handleSubmit(data => {
          void handleSave(data);
        })}
        actions={actions}
      >
        <FormBuilder
          config={formConfig}
          control={control}
          formState={formState}
        />
        {canDelete && (
          <ConfirmDeleteDialog
            open={confirmDeleteOpen}
            deleteHandler={() => handleDeleteVisit()}
            handleClose={() => setConfirmDeleteOpen(false)}
            subject={'visit'}
          />
        )}
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default VisitDialog;
